import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $initialAccountsStore, $initialCardsStore, $profileStore, $remoteConfigStore } from '@lib/store';
import { AccountType, CardKind, UserType } from '@shared/api';

export const TabType = {
  Payment: AccountType.Payment,
  Card: AccountType.Card,
  Rewards: 'Rewards',
} as const;
export type TabType = (typeof TabType)[keyof typeof TabType];

interface UseTabsProps {
  withDebitCard?: boolean;
  withRewardsTab?: boolean;
}

export const useTabs = (props: UseTabsProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const accountsStore = useUnit($initialAccountsStore.store);
  const cardsStore = useUnit($initialCardsStore.store);
  const profileStore = useUnit($profileStore.store);
  const remoteConfigStore = useUnit($remoteConfigStore.store);

  const [tab, setTab] = useState<TabType | null>(null);

  const { t } = useTranslation();

  const loading = useMemo(() => accountsStore.loading || cardsStore.loading, [accountsStore.loading, cardsStore.loading]);
  const hasPayment = useMemo(() => accountsStore.data.some((account) => account.type === AccountType.Payment), [accountsStore.data]);
  const hasCreditCard = useMemo(() => cardsStore.data.some((card) => card.cardKind === CardKind.Credit), [cardsStore.data]);
  const hasDebitCard = useMemo(() => cardsStore.data.some((card) => card.cardKind === CardKind.Debit), [cardsStore.data]);
  const hasCard = useMemo(() => hasCreditCard || hasDebitCard, [hasCreditCard, hasDebitCard]);
  const hasCardTab = useMemo(() => hasCreditCard || (hasDebitCard && !!props.withDebitCard), [hasCreditCard, hasDebitCard, props.withDebitCard]);
  const hasRewards = useMemo(
    () =>
      !!props.withRewardsTab &&
      $remoteConfigStore.events.getValue('RewardsPage', 'boolean') &&
      !!profileStore.data?.accumulateRewards &&
      profileStore.data?.userType === UserType.Admin,
    [props.withRewardsTab, profileStore.data?.accumulateRewards, remoteConfigStore.data],
  );

  useEffect(() => {
    if (loading) return;
    const hash = location.hash.replace('#', '');
    if (hash === TabType.Payment && hasPayment) setTab(TabType.Payment);
    else if (hash === TabType.Card && hasCardTab) setTab(TabType.Card);
    else if (hash === TabType.Rewards && hasRewards) setTab(TabType.Rewards);
    else if (hasPayment && hasCardTab) setTab(TabType.Payment);
    else if (hasPayment) setTab(TabType.Payment);
    else if (hasCardTab) setTab(TabType.Card);
    else if (hasRewards) setTab(TabType.Rewards);
    else setTab(null);
  }, [loading, hasPayment, hasCardTab, hasRewards, location.hash]);

  const changeTabHandler = useCallback((tab: TabType) => {
    setTab(tab);
    navigate(`#${tab}`, { replace: true });
  }, []);

  const tabs = useMemo(() => {
    const items: { label: string; value: TabType }[] = [];
    if (hasPayment) items.push({ label: t('accountType.payments'), value: TabType.Payment });
    if (hasCardTab) items.push({ label: t('accountType.cards'), value: TabType.Card });
    if (hasRewards) items.push({ label: t('sidebar.menu.rewards'), value: TabType.Rewards });
    return items;
  }, [t, hasPayment, hasCardTab, hasRewards]);

  return useMemo(
    () => ({
      loading,
      hasCreditCard,
      hasDebitCard,
      hasCard,
      hasPayment,
      hasRewards,
      tabs,
      tab,
      onChangeTab: changeTabHandler,
    }),
    [loading, hasCreditCard, hasDebitCard, hasCard, hasPayment, hasRewards, tabs, tab, changeTabHandler],
  );
};
